/**
 * Charts (SPEC §7.9). A tab keeps a stack of charts (`view.charts`); each draws the tab's items
 * (or the cards it picks) in one of seven styles. Any style takes any data: the same settings
 * (Group: what each piece is · Count: how big it is · Color by: a second split inside it) give
 * every style what it reads: pieces (Ring, Bars, Mosaic, Flow), days (Commits) or the items in
 * order (Trail, Metro). The app, the Done tab and the chat's tools all use this, so a chart
 * means the same thing everywhere.
 */
import { addDays, dateInTz, daysBetween, formatMonthDay, parseDate, startOfWeek, timeToMinutes } from './dates'
import { displayTitle } from './links'
import { formatFieldValue, formatMoney, hasValue } from './fields'
import { tagLabel } from './tags'
import { topLevelOf } from './tree'
import { filterItems, matchesFilter, narrowToGroup, normalizeView, type ItemsIndex } from './views'
import type { ChartBucket, ChartColorBy, ChartCount, ChartGroup, ChartSpec, ChartStyle, FieldDef, ItemId, ItemSnapshot, SavedView, SavedViewFilter, Tint, UserInfo } from './types'

export const CHART_STYLES: readonly ChartStyle[] = ['ring', 'bars', 'mosaic', 'commits', 'flow', 'trail', 'metro', 'diagram', 'wheel']
export const CHART_STYLE_LABEL: Record<ChartStyle, string> = {
  ring: 'Ring',
  bars: 'Bars',
  mosaic: 'Mosaic',
  commits: 'Commits',
  flow: 'Flow',
  trail: 'Trail',
  metro: 'Metro',
  diagram: 'Diagram',
  wheel: 'Wheel',
}

/**
 * A piece's color (SPEC §7.10): ink when a chart has one series (done solid, open hatched); one of
 * the card colors where pieces are cards or categories; gray for "No area" and the like.
 */
export type ChartColor = Exclude<Tint, 'none'> | 'gray' | 'ink'
/** Card colors handed out in turn, far apart from one another. */
export const CHART_PALETTE: readonly ChartColor[] = ['peach', 'sky', 'sage', 'rose', 'sand', 'azure', 'lilac', 'chartreuse', 'slate']

/** Everything a chart works out from, besides the items. */
export interface ChartCtx {
  today: string
  /** Where "when it got done" is counted (a day where the phone is). */
  tz: string
  weekStart: 0 | 1
  fields: ReadonlyMap<string, FieldDef>
  users: ReadonlyMap<string, UserInfo>
  /** The tab's own colors for its groups (by group key). */
  tints?: Readonly<Record<string, Tint>>
}

export interface ChartPart {
  key: string
  label: string
  value: number
  ids: string[]
  color: ChartColor
}

export interface ChartPiece {
  key: string
  label: string
  /** A detail's value ("food" under #type): shown with its # in legends. */
  detail?: boolean
  /** The items in it (each once). */
  ids: string[]
  value: number
  /** How much of `value` is done (all of it when counting what got done). */
  done: number
  color: ChartColor
  /** Split by Color by (empty when it isn't). */
  parts: ChartPart[]
  /** It stands for one item (grouped by Item): tapping it opens that item. */
  item?: string
  /** A date piece: its first and last day. */
  from?: string
  to?: string
}

export interface ChartDay {
  date: string
  value: number
  ids: string[]
}

export interface ChartStep {
  id: string
  title: string
  date?: string
  time?: string
  endTime?: string
  done: boolean
  color: ChartColor
}

export interface ChartLegend {
  key: string
  label: string
  detail?: boolean
  color: ChartColor
}

export interface ChartData {
  pieces: ChartPiece[]
  /** The colors inside the pieces (Color by), in order; empty when it isn't split. */
  parts: ChartLegend[]
  /** Sum over all pieces, and how much of it is done. */
  total: number
  done: number
  /** Commits: every day in its range (oldest first). */
  days: ChartDay[]
  /** By date: what each piece turned out to be (Automatic picks by how long it runs). */
  bucket?: ChartBucket
  /** Trail and Metro: the items in order (by date and time, then where they sit). */
  steps: ChartStep[]
  /** What the steps lead to: the card the tab shows, when it has a date or time. */
  goal?: ChartStep
  /** How values read: counts, money (in dollars) or a number detail. */
  unit: 'count' | 'money' | 'number'
  /** Diagram: the items it lights up (the tab's), and each one's color when it's colored by something. Wheel: its slices (the tab's open items). */
  items?: string[]
  colors?: Record<string, ChartColor>
}

/**
 * How far a date chart reaches: Fit (0, the default) runs from the first thing in it to today
 * (to the last thing, for what's planned); a number is that many days back to today.
 */
export const RANGE_FIT = 0

/** By date, each piece: a day, a week, a month, or Automatic (by how long it runs). */
export type ChartBucketSetting = ChartBucket | 'auto'

/** The settings a style starts with, and what it reads. */
export function chartDefaults(style: ChartStyle): { group: ChartGroup; count: ChartCount; bucket: ChartBucketSetting; range: number; colorBy: ChartColorBy } {
  switch (style) {
    case 'commits':
      return { group: 'date', count: 'done', bucket: 'day', range: RANGE_FIT, colorBy: 'none' }
    case 'flow':
      return { group: 'date', count: 'done', bucket: 'auto', range: RANGE_FIT, colorBy: 'card' }
    case 'trail':
    case 'metro':
      return { group: 'none', count: 'items', bucket: 'auto', range: RANGE_FIT, colorBy: 'none' }
    default:
      return { group: 'card', count: 'items', bucket: 'auto', range: RANGE_FIT, colorBy: 'none' }
  }
}

/** Which settings a style uses (the others are kept, unused, for when it switches back). */
export function chartUses(style: ChartStyle): { group: boolean; count: boolean; colorBy: boolean } {
  switch (style) {
    case 'commits':
      return { group: false, count: true, colorBy: false }
    case 'trail':
    case 'metro':
      return { group: false, count: false, colorBy: true }
    case 'diagram':
      return { group: false, count: false, colorBy: true }
    case 'wheel':
      return { group: false, count: false, colorBy: false }
    case 'bars':
    case 'flow':
    case 'mosaic':
      return { group: true, count: true, colorBy: true }
    default:
      return { group: true, count: true, colorBy: false }
  }
}

/** A chart's settings with its style's defaults filled in. */
export function chartSettings(spec: ChartSpec) {
  const d = chartDefaults(spec.style)
  const group = spec.style === 'commits' ? 'date' : (spec.group ?? d.group)
  return {
    group,
    count: spec.count ?? d.count,
    bucket: spec.style === 'commits' ? 'day' : (spec.bucket ?? d.bucket),
    range: !spec.range || spec.range < 0.5 ? RANGE_FIT : Math.min(732, Math.round(spec.range)),
    colorBy: chartUses(spec.style).colorBy ? (spec.colorBy ?? d.colorBy) : 'none',
  } as { group: ChartGroup; count: ChartCount; bucket: ChartBucketSetting; range: number; colorBy: ChartColorBy }
}

export function isChartSpec(v: unknown): v is ChartSpec {
  return !!v && typeof v === 'object' && typeof (v as ChartSpec).id === 'string' && CHART_STYLES.includes((v as ChartSpec).style)
}

/**
 * The items a chart on a tab reads: the tab's, finished ones included so it can show progress
 * (a tab about finished things keeps to those), or the cards the chart picks instead.
 */
export function chartItems(idx: ItemsIndex, view: SavedView, spec: Pick<ChartSpec, 'shows' | 'filter'>, today: string): string[] {
  const v = normalizeView(view)
  const f: SavedViewFilter = { ...v.filter, done: v.filter.done === 'done' ? 'done' : 'any' }
  if (spec.shows?.length) f.ancestors = spec.shows
  return chartFiltered(idx, narrowToGroup(idx, filterItems(idx, f, today), v.groupBy), spec, today)
}

/** What's left of `ids` after a chart's own filter (none set: all of them). */
export function chartFiltered(idx: ItemsIndex, ids: string[], spec: Pick<ChartSpec, 'filter'>, today: string): string[] {
  const f = spec.filter
  if (!f || !Object.keys(f).length) return ids
  return ids.filter((id) => {
    const it = idx.items.get(id)
    return !!it && matchesFilter(idx, it, f, today)
  })
}

// ---------------------------------------------------------------------------
// Working it out

const title = (idx: ItemsIndex, id: string) => displayTitle(idx.items.get(id)?.title ?? '', (x) => idx.items.get(x)?.title) || 'Untitled'
const firstName = (name: string | undefined) => (name ?? '').split(' ')[0] || 'Someone'

/** An item's amount for a money or number detail (money in dollars), or 0. */
export function amountOf(it: ItemSnapshot, key: string): number {
  const v = it.props[key]
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (v && typeof v === 'object' && 'cents' in v && Number.isFinite((v as { cents: number }).cents)) return (v as { cents: number }).cents / 100
  return 0
}

interface Key {
  key: string
  label: string
  detail?: boolean
}

/** The groups an item sits in for a grouping (several for tags: an item in two areas is in both). */
function keysOf(idx: ItemsIndex, it: ItemSnapshot, by: ChartGroup | ChartColorBy, ctx: ChartCtx): Key[] {
  if (by === 'none') return [{ key: '*', label: 'All' }]
  if (by === 'item') return [{ key: it.id, label: title(idx, it.id) }]
  if (by === 'card') {
    const top = topLevelOf(idx.tree, it.id)
    return [{ key: top, label: title(idx, top) }]
  }
  if (by === 'assignee') return it.assignee ? [{ key: it.assignee, label: firstName(ctx.users.get(it.assignee)?.name) }] : [{ key: 'none', label: 'Anyone' }]
  if (by === 'tag') {
    const tops = [...new Set(it.tags.map((t) => t.split('/')[0]!))]
    return tops.length ? tops.map((t) => ({ key: t, label: tagLabel(t), detail: true })) : [{ key: '', label: 'No details' }]
  }
  if (by.startsWith('tag:')) {
    const p = by.slice(4)
    const next = new Set<string>()
    for (const t of it.tags) if (t.startsWith(p + '/')) next.add(t.slice(p.length + 1).split('/')[0]!)
    const name = tagLabel(p.split('/').pop()!).toLowerCase()
    return next.size ? [...next].map((n) => ({ key: `${p}/${n}`, label: tagLabel(n), detail: true })) : [{ key: '', label: `No ${name}` }]
  }
  if (by.startsWith('field:')) {
    const k = by.slice(6)
    const v = it.props[k]
    const def = ctx.fields.get(k)
    return hasValue(v) ? [{ key: formatFieldValue(def, v), label: formatFieldValue(def, v) }] : [{ key: '', label: `No ${(def?.label ?? k).toLowerCase()}` }]
  }
  // 'date' outside a date chart: by the day it's on.
  return it.date ? [{ key: it.date, label: formatMonthDay(it.date) }] : [{ key: '', label: 'No date' }]
}

const EMPTY_KEYS = new Set(['', 'none'])

/** One thing to add up: an item (or one completion of it), with what it's worth and its date. */
interface Entry {
  id: string
  value: number
  done: boolean
  date?: string
}

function entriesFor(idx: ItemsIndex, ids: string[], count: ChartCount, byDate: boolean, tz: string): Entry[] {
  const out: Entry[] = []
  const amountKey = count.startsWith('amount:') ? count.slice(7) : null
  for (const id of ids) {
    const it = idx.items.get(id)
    if (!it) continue
    const done = it.done === true
    const lastDone = it.completions.length ? dateInTz(it.completions[it.completions.length - 1]!.at, tz) : it.completedAt ? dateInTz(it.completedAt, tz) : undefined
    if (count === 'done') {
      if (byDate) {
        // Every time it got done (a repeating chore counts each time).
        for (const c of it.completions) out.push({ id, value: 1, done: true, date: dateInTz(c.at, tz) })
        if (!it.completions.length && done && lastDone) out.push({ id, value: 1, done: true, date: lastDone })
      } else out.push({ id, value: done ? 1 : 0, done })
    } else if (amountKey) {
      const v = amountOf(it, amountKey)
      if (!v) continue
      out.push({ id, value: v, done, date: it.date ?? lastDone })
    } else out.push({ id, value: 1, done, date: it.date })
  }
  return out
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function bucketStart(date: string, bucket: ChartBucket, weekStart: 0 | 1): string {
  if (bucket === 'week') return startOfWeek(date, weekStart)
  if (bucket === 'month') return `${date.slice(0, 7)}-01`
  return date
}

function bucketEnd(start: string, bucket: ChartBucket): string {
  if (bucket === 'week') return addDays(start, 6)
  if (bucket === 'month') {
    const { y, m } = parseDate(start)
    const next = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`
    return addDays(next, -1)
  }
  return start
}

function bucketLabel(start: string, bucket: ChartBucket): string {
  const { m } = parseDate(start)
  return bucket === 'month' ? MONTHS[m - 1]! : formatMonthDay(start)
}

/**
 * The days a date chart covers. Fit: from the first thing in it to today, or to the last thing
 * for what's planned (at most two years). A range: that many days back to today, and on to
 * what's planned within as long again.
 */
function dateWindow(entries: Entry[], s: { count: ChartCount; range: number }, today: string): { start: string; end: string } {
  const dates = entries.map((e) => e.date).filter((d): d is string => !!d)
  const planned = s.count !== 'done'
  if (s.range === RANGE_FIT) {
    if (!dates.length) return { start: today, end: today }
    const first = dates.reduce((a, d) => (d < a ? d : a))
    const last = dates.reduce((a, d) => (d > a ? d : a))
    const end = planned || last > today ? last : today
    return { start: first < addDays(end, -731) ? addDays(end, -731) : first, end }
  }
  const ahead = addDays(today, s.range)
  let end = today
  if (planned) for (const d of dates) if (d > end && d <= ahead) end = d
  return { start: addDays(today, -(s.range - 1)), end }
}

/** Automatic pieces: a day each for up to three weeks, a week each up to about five months, else a month. */
function autoBucket(start: string, end: string): ChartBucket {
  const days = daysBetween(start, end) + 1
  return days <= 21 ? 'day' : days <= 150 ? 'week' : 'month'
}

/** How many pieces a style shows before the rest go into "Other". */
const MAX_PIECES: Record<ChartStyle, number> = { ring: 8, bars: 8, mosaic: 24, flow: 12, commits: 0, trail: 0, metro: 0, diagram: 0, wheel: 0 }

/** Give each key a color: a card's own tint, the tab's color for a group, else the next unused one. */
function colorer(idx: ItemsIndex, by: ChartGroup | ChartColorBy, ctx: ChartCtx) {
  const used = new Set<ChartColor>()
  let next = 0
  const given = new Map<string, ChartColor>()
  const own = (key: string): ChartColor | null => {
    if (EMPTY_KEYS.has(key) || key === '__other') return 'gray'
    const t = ctx.tints?.[key] ?? (by === 'card' || by === 'item' ? idx.items.get(key)?.tint : undefined)
    return t && t !== 'none' ? t : null
  }
  return (key: string): ChartColor => {
    const had = given.get(key)
    if (had) return had
    let c = own(key)
    if (!c) {
      // The next palette color nobody has yet (round again once they're all taken).
      for (let i = 0; i < CHART_PALETTE.length && !c; i++) {
        const p = CHART_PALETTE[(next + i) % CHART_PALETTE.length]!
        if (!used.has(p)) {
          c = p
          next = (next + i + 1) % CHART_PALETTE.length
        }
      }
      if (!c) c = CHART_PALETTE[next++ % CHART_PALETTE.length]!
    }
    used.add(c)
    given.set(key, c)
    return c
  }
}

/** Work a chart out: its pieces, days or steps, from `ids` (see chartItems). `goal`: the card a tab shows. */
export function chartData(idx: ItemsIndex, ids: string[], spec: ChartSpec, ctx: ChartCtx, opts: { goal?: ItemId | null } = {}): ChartData {
  const s = chartSettings(spec)
  const amountKey = s.count.startsWith('amount:') ? s.count.slice(7) : null
  const def = amountKey ? ctx.fields.get(amountKey) : undefined
  const unit: ChartData['unit'] = !amountKey ? 'count' : def?.type === 'number' ? 'number' : 'money'
  const out: ChartData = { pieces: [], parts: [], total: 0, done: 0, days: [], steps: [], unit }
  const style = spec.style

  // A wheel to spin (SPEC §7.9): a slice for each open item, all the same size.
  if (style === 'wheel') {
    out.items = ids.filter((id) => idx.items.get(id)?.done !== true)
    out.total = out.items.length
    return out
  }

  // A diagram draws itself (SPEC §7.11): it only needs to know which items are the tab's.
  if (style === 'diagram') {
    out.items = ids
    out.total = ids.length
    out.done = ids.filter((id) => idx.items.get(id)?.done === true).length
    // Colored by a detail (or a person…): each item's color, and the legend.
    if (s.colorBy !== 'none') {
      const color = colorer(idx, s.colorBy, ctx)
      const colors: Record<string, ChartColor> = {}
      const legend = new Map<string, ChartLegend>()
      for (const id of ids) {
        const it = idx.items.get(id)
        const k = it ? keysOf(idx, it, s.colorBy, ctx)[0] : undefined
        if (!k) continue
        colors[id] = color(k.key)
        if (!legend.has(k.key)) legend.set(k.key, { key: k.key, label: k.label, ...(k.detail ? { detail: true } : {}), color: colors[id] })
      }
      out.colors = colors
      out.parts = [...legend.values()]
    }
    return out
  }

  if (style === 'trail' || style === 'metro') {
    out.steps = chartSteps(idx, ids, s.colorBy, ctx, opts.goal ?? null)
    const goal = opts.goal ? idx.items.get(opts.goal) : undefined
    if (goal && (goal.date || goal.time)) out.goal = { id: goal.id, title: title(idx, goal.id), date: goal.date, time: goal.time, endTime: goal.endTime, done: goal.done === true, color: 'ink' }
    out.total = out.steps.length
    out.done = out.steps.filter((x) => x.done).length
    return out
  }

  const byDate = s.group === 'date'
  const entries = entriesFor(idx, ids, s.count, byDate || style === 'commits', ctx.tz)

  if (style === 'commits') {
    // Every day it covers, oldest first.
    const { start, end } = dateWindow(entries, s, ctx.today)
    const byDay = new Map<string, ChartDay>()
    for (let d = start; d <= end; d = addDays(d, 1)) byDay.set(d, { date: d, value: 0, ids: [] })
    for (const e of entries) {
      const day = e.date ? byDay.get(e.date) : undefined
      if (!day) continue
      day.value += e.value
      if (!day.ids.includes(e.id)) day.ids.push(e.id)
    }
    out.days = [...byDay.values()]
    out.total = out.days.reduce((a, d) => a + d.value, 0)
    out.done = s.count === 'done' ? out.total : entries.filter((e) => e.done && e.date && e.date >= start && e.date <= end).reduce((a, e) => a + e.value, 0)
    return out
  }

  // Pieces: by date (a day, week or month each, every one in the range), or by a grouping.
  type Acc = { key: string; label: string; detail?: boolean; entries: Entry[]; from?: string; to?: string }
  const acc = new Map<string, Acc>()
  if (byDate) {
    const { start, end } = dateWindow(entries, s, ctx.today)
    const bucket = s.bucket === 'auto' ? autoBucket(start, end) : s.bucket
    out.bucket = bucket
    let first = bucketStart(start, bucket, ctx.weekStart)
    // A Flow needs two to draw a line between: the one before, when it's all in one.
    if (style === 'flow' && first === bucketStart(end, bucket, ctx.weekStart)) first = bucketStart(addDays(first, -1), bucket, ctx.weekStart)
    for (let b = first; b <= end; b = addDays(bucketEnd(b, bucket), 1)) acc.set(b, { key: b, label: bucketLabel(b, bucket), entries: [], from: b, to: bucketEnd(b, bucket) })
    for (const e of entries) {
      if (!e.date) continue
      acc.get(bucketStart(e.date, bucket, ctx.weekStart))?.entries.push(e)
    }
  } else {
    for (const e of entries) {
      const it = idx.items.get(e.id)!
      for (const k of keysOf(idx, it, s.group, ctx)) {
        let a = acc.get(k.key)
        if (!a) acc.set(k.key, (a = { ...k, entries: [] }))
        a.entries.push(e)
      }
    }
  }
  let list = [...acc.values()].map((a) => ({ ...a, value: a.entries.reduce((n, e) => n + e.value, 0), done: a.entries.filter((e) => e.done).reduce((n, e) => n + e.value, 0) }))
  if (!byDate) {
    // Biggest first; "No area" and the like last; an Item chart keeps only items with a value.
    list = list.filter((a) => a.value > 0 || s.count === 'done').sort((a, b) => Number(EMPTY_KEYS.has(a.key)) - Number(EMPTY_KEYS.has(b.key)) || b.value - a.value || a.label.localeCompare(b.label))
    const max = MAX_PIECES[style]
    if (max && list.length > max) {
      const rest = list.slice(max - 1)
      list = [
        ...list.slice(0, max - 1),
        {
          key: '__other',
          label: `${rest.length} more`,
          entries: rest.flatMap((r) => r.entries),
          value: rest.reduce((n, r) => n + r.value, 0),
          done: rest.reduce((n, r) => n + r.done, 0),
        },
      ]
    }
  }
  const colorOf = colorer(idx, s.group, ctx)
  const partColor = colorer(idx, s.colorBy, ctx)
  // A piece that's one item: its Color by's color, else its card's (one color for them all).
  const itemColor = (id: string): ChartColor => {
    if (s.colorBy !== 'none') return partColor(keysOf(idx, idx.items.get(id)!, s.colorBy, ctx)[0]!.key)
    const t = idx.items.get(topLevelOf(idx.tree, id))?.tint
    return t && t !== 'none' ? t : 'ink'
  }
  const partLegend = new Map<string, ChartLegend>()
  out.pieces = list.map((a) => {
    const ids = [...new Set(a.entries.map((e) => e.id))]
    const parts: ChartPart[] = []
    if (s.colorBy !== 'none') {
      const m = new Map<string, ChartPart>()
      for (const e of a.entries) {
        for (const k of keysOf(idx, idx.items.get(e.id)!, s.colorBy, ctx)) {
          let p = m.get(k.key)
          if (!p) {
            m.set(k.key, (p = { key: k.key, label: k.label, value: 0, ids: [], color: partColor(k.key) }))
            if (!partLegend.has(k.key)) partLegend.set(k.key, { key: k.key, label: k.label, detail: k.detail, color: p.color })
          }
          p.value += e.value
          if (!p.ids.includes(e.id)) p.ids.push(e.id)
        }
      }
      parts.push(...[...m.values()].sort((x, y) => Number(EMPTY_KEYS.has(x.key)) - Number(EMPTY_KEYS.has(y.key)) || y.value - x.value))
    }
    return {
      key: a.key,
      label: a.label,
      ...(a.detail ? { detail: true } : {}),
      ids,
      value: a.value,
      done: a.done,
      color: byDate ? 'ink' : s.group === 'item' && a.key !== '__other' ? itemColor(a.key) : colorOf(a.key),
      parts,
      ...(s.group === 'item' && a.key !== '__other' ? { item: a.key } : {}),
      ...(a.from ? { from: a.from, to: a.to } : {}),
    }
  })
  // The same order of colors in every piece, biggest overall first.
  const totals = new Map<string, number>()
  for (const p of out.pieces) for (const x of p.parts) totals.set(x.key, (totals.get(x.key) ?? 0) + x.value)
  out.parts = [...partLegend.values()].sort((a, b) => Number(EMPTY_KEYS.has(a.key)) - Number(EMPTY_KEYS.has(b.key)) || (totals.get(b.key) ?? 0) - (totals.get(a.key) ?? 0))
  const rank = new Map(out.parts.map((p, i) => [p.key, i]))
  for (const p of out.pieces) p.parts.sort((a, b) => rank.get(a.key)! - rank.get(b.key)!)
  out.total = out.pieces.reduce((n, p) => n + p.value, 0)
  out.done = out.pieces.reduce((n, p) => n + p.done, 0)
  return out
}

/** Where items sit, top to bottom (a card's rows in their order), for "in order". */
const orderCache = new WeakMap<object, Map<string, number>>()
function treeOrder(idx: ItemsIndex): Map<string, number> {
  const had = orderCache.get(idx.tree)
  if (had) return had
  const out = new Map<string, number>()
  const walk = (parent: string | null) => {
    for (const id of idx.tree.childrenOf.get(parent) ?? []) {
      out.set(id, out.size)
      walk(id)
    }
  }
  walk(null)
  orderCache.set(idx.tree, out)
  return out
}

/** The items as steps: by date and time (undated last), then where they sit. */
export function chartSteps(idx: ItemsIndex, ids: string[], colorBy: ChartColorBy, ctx: ChartCtx, goal: ItemId | null): ChartStep[] {
  const order = treeOrder(idx)
  const colorOf = colorer(idx, colorBy, ctx)
  return ids
    .filter((id) => id !== goal)
    .map((id) => idx.items.get(id)!)
    .filter(Boolean)
    .sort(
      (a, b) =>
        (a.date ? 0 : 1) - (b.date ? 0 : 1) ||
        (a.date ?? '').localeCompare(b.date ?? '') ||
        (a.time ? 0 : 1) - (b.time ? 0 : 1) ||
        (a.time ?? '').localeCompare(b.time ?? '') ||
        (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0),
    )
    .map((it) => ({
      id: it.id,
      title: title(idx, it.id),
      ...(it.date ? { date: it.date } : {}),
      ...(it.time ? { time: it.time } : {}),
      ...(it.endTime ? { endTime: it.endTime } : {}),
      done: it.done === true,
      color: colorBy === 'none' ? 'ink' : colorOf(keysOf(idx, it, colorBy, ctx)[0]!.key),
    }))
}

/**
 * When the steps happen, in minutes from midnight, once they're on one day with times (two or
 * more): each runs until its end time, else the next step, else the goal, else half an hour.
 * `spare`: the time left over (gaps between steps, and before the goal). Null when they aren't timed.
 */
export function stepTimes(
  steps: ReadonlyArray<Pick<ChartStep, 'date' | 'time' | 'endTime'>>,
  goal?: Pick<ChartStep, 'date' | 'time'> | null,
): { date: string | null; starts: Array<number | null>; ends: Array<number | null>; from: number; to: number; goal: number | null; spare: number | null } | null {
  const timed = steps.filter((x) => x.time)
  const dates = new Set(timed.map((x) => x.date ?? ''))
  if (timed.length < 2 || dates.size !== 1) return null
  const date = [...dates][0] || null
  const starts = steps.map((x) => (x.time && (x.date ?? '') === (date ?? '') ? timeToMinutes(x.time) : null))
  const g = goal?.time && (goal.date ?? date ?? '') === (date ?? '') ? timeToMinutes(goal.time) : null
  const ends = starts.map((st, i) => {
    if (st === null) return null
    const own = steps[i]!.endTime ? timeToMinutes(steps[i]!.endTime!) : null
    if (own !== null && own > st) return own
    const next = starts.slice(i + 1).find((x): x is number => x !== null && x > st)
    return next ?? (g !== null && g > st ? g : st + 30)
  })
  const known = starts.filter((x): x is number => x !== null)
  const lastEnd = Math.max(...ends.filter((x): x is number => x !== null))
  // Time nobody planned for: the gaps between a step's end and the next start, and before the goal.
  let spare: number | null = null
  const hasEnds = steps.some((x) => x.endTime)
  if (hasEnds || g !== null) {
    spare = 0
    for (let i = 0; i < starts.length; i++) {
      const e = ends[i] ?? null
      const next = starts.slice(i + 1).find((x): x is number => x !== null)
      if (e !== null && next !== undefined && next > e) spare += next - e
    }
    if (g !== null && g > lastEnd) spare += g - lastEnd
  }
  return { date, starts, ends, from: Math.min(...known), to: Math.max(lastEnd, g ?? 0), goal: g, spare }
}

// ---------------------------------------------------------------------------
// Names and numbers

/** A value as the chart says it ("12", "$1,580", "3.5"). */
export function formatChartValue(v: number, unit: ChartData['unit']): string {
  if (unit === 'money') return formatMoney({ cents: Math.round(v * 100), currency: 'USD' })
  if (unit === 'number') return Number.isInteger(v) ? String(v) : v.toFixed(1)
  return String(Math.round(v))
}

/** A chart's name: its own, or one from its settings ("By type", "Done each week"). `bucket`: what Automatic turned out to be. */
export function chartTitle(spec: ChartSpec, fields: ReadonlyMap<string, FieldDef>, bucket?: ChartBucket): string {
  if (spec.title?.trim()) return spec.title.trim()
  const s = chartSettings(spec)
  const amount = s.count.startsWith('amount:') ? (fields.get(s.count.slice(7))?.label ?? 'Amount') : null
  if (spec.style === 'trail' || spec.style === 'metro') return 'Steps'
  if (spec.style === 'diagram') return 'Diagram'
  if (spec.style === 'wheel') return 'Spin'
  if (spec.style === 'commits') return s.count === 'done' ? 'Done each day' : amount ? `${amount} by day` : 'By day'
  if (s.group === 'date') {
    const b = s.bucket === 'auto' ? bucket : s.bucket
    if (!b) return s.count === 'done' ? 'Done over time' : amount ? `${amount} over time` : 'By date'
    return s.count === 'done' ? `Done each ${b}` : amount ? `${amount} by ${b}` : `By ${b}`
  }
  const by =
    s.group === 'card'
      ? 'card'
      : s.group === 'assignee'
        ? 'person'
        : s.group === 'tag'
          ? 'detail'
          : s.group.startsWith('tag:')
            ? tagLabel(s.group.slice(4).split('/').pop()!).toLowerCase()
            : s.group.startsWith('field:')
              ? (fields.get(s.group.slice(6))?.label ?? 'detail').toLowerCase()
              : null
  if (s.group === 'item') return amount ?? 'Each item'
  if (s.group === 'none') return amount ? `${amount} in all` : s.count === 'done' ? 'Done' : 'All of it'
  return amount ? `${amount} by ${by}` : s.count === 'done' ? `Done by ${by}` : `By ${by}`
}

// ---------------------------------------------------------------------------
// Ideas: charts that would suit what a tab holds

export interface ChartIdea {
  key: string
  spec: Omit<ChartSpec, 'id'>
  /** Why it's suggested, in a few words ("These items have amounts"). */
  reason: string
}

/** What makes two charts the same idea: the style and the settings it reads. */
export function chartIdeaKey(spec: Pick<ChartSpec, 'style' | 'group' | 'count' | 'colorBy'>): string {
  const s = chartSettings(spec as ChartSpec)
  const u = chartUses(spec.style)
  return [spec.style, u.group ? s.group : '', u.count ? s.count : '', u.colorBy ? s.colorBy : ''].join('|')
}

/** Marks an idea that was chosen (added): it isn't suggested there again. */
export const IDEA_TAKEN = -1

/** An idea that was seen with `seen` items stays quiet until the tab has changed a lot; one chosen, for good. */
export function ideaIsQuiet(seen: number | undefined, now: number): boolean {
  if (seen === undefined) return false
  if (seen < 0) return true
  return now < seen * 1.5 + 3 && now > seen / 2
}

/**
 * Charts that would suit these items, best first, leaving out the ones the stack has and the
 * ones already seen (until the tab changes a lot). `pinned`: the card the tab shows.
 */
export function chartIdeas(
  idx: ItemsIndex,
  ids: string[],
  ctx: ChartCtx,
  opts: { existing?: ChartSpec[]; seen?: Readonly<Record<string, number>>; pinned?: ItemId | null; all?: boolean } = {},
): ChartIdea[] {
  const items = ids.map((id) => idx.items.get(id)).filter((x): x is ItemSnapshot => !!x && x.id !== opts.pinned)
  const n = items.length
  const ideas: ChartIdea[] = []
  if (!n) return ideas
  // Steps with times on one day (today or later): a trail to where they lead.
  const byDate = new Map<string, number>()
  for (const it of items) if (it.time && it.date && it.date >= addDays(ctx.today, -1)) byDate.set(it.date, (byDate.get(it.date) ?? 0) + 1)
  if ([...byDate.values()].some((c) => c >= 3)) ideas.push({ key: '', spec: { style: 'trail' }, reason: 'These steps have times' })
  // A detail most of them share, with a few values: a ring by it.
  const prefixes = new Map<string, Set<string>>()
  const having = new Map<string, number>()
  for (const it of items) {
    const seenHere = new Set<string>()
    for (const t of it.tags) {
      const [p, next] = t.split('/')
      if (!p || !next) continue
      if (!prefixes.has(p)) prefixes.set(p, new Set())
      prefixes.get(p)!.add(next)
      if (!seenHere.has(p)) having.set(p, (having.get(p) ?? 0) + 1)
      seenHere.add(p)
    }
  }
  const shared = [...having.entries()].filter(([p, c]) => c >= Math.max(3, n * 0.4) && prefixes.get(p)!.size >= 2 && prefixes.get(p)!.size <= 8).sort((a, b) => b[1] - a[1])[0]
  // Amounts: what they add up to, item by item.
  const money = [...ctx.fields.values()].filter((d) => d.type === 'money' || d.type === 'number')
  const withAmount = money.map((d) => ({ d, c: items.filter((it) => amountOf(it, d.key)).length })).sort((a, b) => b.c - a.c)[0]
  if (withAmount && withAmount.c >= 3)
    ideas.push({
      key: '',
      spec: { style: 'mosaic', group: 'item', count: `amount:${withAmount.d.key}`, ...(shared ? { colorBy: `tag:${shared[0]}` as const } : {}) },
      reason: `These items have ${withAmount.d.label.toLowerCase()}s`,
    })
  if (shared) ideas.push({ key: '', spec: { style: 'ring', group: `tag:${shared[0]}` }, reason: `Most of these have a #${shared[0]}` })
  // Things that get checked off here: when they got done.
  const since = addDays(ctx.today, -56)
  const done = items.reduce((a, it) => a + it.completions.filter((c) => dateInTz(c.at, ctx.tz) >= since).length, 0)
  if (done >= 6) ideas.push({ key: '', spec: { style: 'commits' }, reason: 'Things here get checked off' })
  // From several cards: how much each holds.
  const cards = new Set(items.map((it) => topLevelOf(idx.tree, it.id)))
  if (cards.size >= 2 && n >= 6) ideas.push({ key: '', spec: { style: 'bars', group: 'card' }, reason: `These come from ${cards.size} cards` })
  // Shared out between people: who has what.
  const people = new Set(items.map((it) => it.assignee).filter(Boolean))
  if (people.size >= 2) ideas.push({ key: '', spec: { style: 'bars', group: 'assignee' }, reason: 'These are split between people' })
  // Anything with a checkbox: how far along it is.
  if (!ideas.length && items.some((it) => it.done !== null)) ideas.push({ key: '', spec: { style: 'ring', group: 'card' }, reason: 'How far along it is' })
  // What the stack already has: a chart of the same thing however it's been set up since (the
  // same groups in any style that groups; Trail and Metro are one kind, Commits another).
  const kind = (spec: Pick<ChartSpec, 'style' | 'group'>) => {
    if (spec.style === 'trail' || spec.style === 'metro') return 'steps'
    return chartUses(spec.style).group ? `pieces|${chartSettings(spec as ChartSpec).group}` : spec.style
  }
  const have = new Set((opts.existing ?? []).map(kind))
  return ideas.map((i) => ({ ...i, key: chartIdeaKey(i.spec) })).filter((i) => !have.has(kind(i.spec)) && ((opts.all && (opts.seen?.[i.key] ?? 0) >= 0) || !ideaIsQuiet(opts.seen?.[i.key], n)))
}

// ---------------------------------------------------------------------------
// The Done tab

/** The Done tab's charts: its own, or the standard Commits of what got done. */
export function doneTabCharts(saved: ChartSpec[] | null | undefined): ChartSpec[] {
  return saved ?? [{ id: 'done-commits', style: 'commits', count: 'done' }]
}
